/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import axios from "axios";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FilterField,
  labelId,
  ParsedFilter as OldParsed,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import { useNavigate } from "react-router-dom";
import {
  SdwanHub,
  sdwanHubColumns as baseColumns,
  sdwanHubColumns,
  shapeSdwanHubData,
} from "./SdwanHubData";
import { useAppSelector } from "@redux/hooks";
import { makeSdwanHubFields } from "./SdwanHubFieldSpec";
import GenericButton from "@ui-components/Button/GenericButton";
import { fetchFortimanager, fetchSdwanHubList } from "./request/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { ListPageSkeleton, RefreshingIndicator } from "@shared/components/ListLoading";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { ConnectionsCell } from "../fwaas/ConnectionCell";
import AllConnectionsModal from "../fwaas/AllConectionsModal";
import CredentialsModal from "../fwaas/CredentialsModal";
import {
  buildConnItemPredicate,
  canonicalStatus,
  connectionIndex,
  connectionKeyMatches,
  ConnectionType,
  parseConnToken,
  RENDER_ORDER,
  statusLabel,
} from "../fwaas/ConnectionsHelpers";
import { buildDetailPath, serviceDetailPath } from "@shared/navigation/tabs/detailRoutes";

const SDWAN_HUB_MANAGEMENT_BANNER =
  "To review your SDWAN-Hub configuration, open your administration module. This will launch a new tab where you can view your settings. " +
  "If your role allows, you may also make changes from that page. Please use the username and password provided below this message to sign in.";

type FortiManagerData = string | { accessUrl?: string; url?: string };

type FortiManagerResp = {
  success?: boolean;
  message?: string;
  data?: FortiManagerData;
  url?: string;
};

const NO_SDWAN_HUBS: SdwanHub[] = [];

export default function SdwanHubPage() {
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();

  // Cached per tenant: coming back to the list paints the last answer at once and
  // refreshes it underneath.
  const {
    data: sdwanHubList = NO_SDWAN_HUBS,
    error: fetchError,
    isLoading: isLoadingList,
    isRefreshing: isRefreshingList,
  } = useCachedFetch(tenantId ? `kontroledge/bbaas/sdwan-hub?tenantId=${tenantId}` : null, async () => {
    const { status, data } = await fetchSdwanHubList(api, tenantId).catch((err: unknown) => {
      // axios rejects non-2xx: show the backend's message rather than axios' generic one.
      const serverMessage = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      throw serverMessage ? new Error(serverMessage) : err;
    });
    if (status >= 400 || data?.success === false) {
      throw new Error(data?.message || "Unable to load SDWAN-Hub services");
    }
    return Array.isArray(data?.data) ? shapeSdwanHubData(data.data) : [];
  });
  const [fmUrl, setFmUrl] = useState<string | null>(null);
  const [isFetchingFm, setIsFetchingFm] = useState(false);
  const [connectionsModalRow, setConnectionsModalRow] =
    useState<SdwanHub | null>(null);
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [credentialsModalRow, setCredentialsModalRow] =
    useState<SdwanHub | null>(null);
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const navigate = useNavigate();

  const sdwanHubFields = useMemo(
    () => makeSdwanHubFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of sdwanHubFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [sdwanHubFields]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<SdwanHub>>();
    for (const f of sdwanHubFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [sdwanHubFields]);

  const suggestedKeys = useMemo(() => {
    const headers = sdwanHubColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }

    const extra = ["Service ID", "Status", "Connection"];
    for (const k of extra) {
      if (!headers.includes(k)) headers.push(k);
    }
    return headers;
  }, [sdwanHubColumns, isViewingAllCustomers]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  type StringOp = ":" | "=";

  const predicate = useMemo(() => {
    const filters = normalizedFilters;
    const norm = (x: unknown) => String(x ?? "").toLowerCase();
    const matchOp = (hay: string, needle: string, op: StringOp = ":") =>
      !needle ? true : op === "=" ? hay === needle : hay.includes(needle);
    const toStringOp = (o?: ":" | "=" | ">" | "<" | ">=" | "<="): StringOp =>
      o === "=" ? "=" : ":";

    const serviceKeyMatches = (k: string) => {
      const kk = k.replace(/\s+/g, "").toLowerCase();
      return kk === "serviceid" || kk === "service-id";
    };

    const connTokens = filters
      .filter((f) => f.key && connectionKeyMatches(f.key))
      .map((f) => parseConnToken(String(f.value ?? ""), toStringOp(f.op)));
    const connItemPredicate = buildConnItemPredicate(connTokens);

    return (row: SdwanHub) => {
      if (!filters.length) return true;

      const rowServiceId = norm(row.serviceId);
      const rowStatusCanon = canonicalStatus(row.status);
      const rowStatusLabel = statusLabel(row.status).toLowerCase();
      const connIdx = connectionIndex(row);

      const custName = norm(row.customer?.name);
      const custId = norm(row.customer?.chargebeeCustomerId);

      return filters.every((f) => {
        const key = (f.key || "").trim().toLowerCase();
        const valueRaw = String(f.value ?? "");
        const value = valueRaw.toLowerCase();
        const sop = toStringOp(f.op);

        if (!key) {
          return (
            matchOp(rowServiceId, value, ":") ||
            matchOp(rowStatusLabel, value, ":") ||
            matchOp(rowStatusCanon, value, ":") ||
            matchOp(connIdx, value, ":") ||
            matchOp(custName, value, ":") ||
            matchOp(custId, value, ":")
          );
        }

        if (serviceKeyMatches(key)) {
          return matchOp(rowServiceId, value, sop);
        }

        if (key === "status") {
          if (sop === "=") {
            return canonicalStatus(valueRaw) === rowStatusCanon;
          }
          return (
            matchOp(rowStatusLabel, value, ":") ||
            matchOp(rowStatusCanon, value, ":")
          );
        }

        if (connectionKeyMatches(key)) {
          const tokenMatcher = parseConnToken(valueRaw, sop);
          const perTokenFn = buildConnItemPredicate([tokenMatcher]);
          const b = row.connections ?? {};
          for (const t of RENDER_ORDER) {
            const list = (b as any)[t] as
              | { id: string; subscriptionId: string }[]
              | undefined;
            if (!list?.length) continue;
            if (perTokenFn && list.some((it) => perTokenFn(t, it))) return true;
          }
          return false;
        }

        if (key === "customer") {
          return matchOp(custName, value, sop) || matchOp(custId, value, sop);
        }

        return false;
      });
    };
  }, [normalizedFilters]);

  const connItemFilterFn = useMemo(() => {
    const filters = normalizedFilters;
    const connTokens = filters
      .filter((f) => f.key && connectionKeyMatches(f.key))
      .map((f) =>
        parseConnToken(String(f.value ?? ""), f.op === "=" ? "=" : ":")
      );
    return buildConnItemPredicate(connTokens);
  }, [normalizedFilters]);

  // A branch lives under its hub, so its address needs the hub's id too.
  const connectionHref = useCallback(
    (type: ConnectionType, id: string, row: SdwanHub) =>
      type === "branch"
        ? buildDetailPath("sdwanBranch", { sdwanId: row.id, branchId: id })
        : serviceDetailPath(type, id),
    []
  );

  const handleConnectionClick = useCallback(
    (type: ConnectionType, idFromCell: string, row: SdwanHub) => {
      const id = idFromCell ?? "";
      // Branch needs the parent hub id too → go straight to the branch detail.
      if (type === "branch") {
        navigate(buildDetailPath("sdwanBranch", { sdwanId: row.id, branchId: id }));
        return;
      }
      navigate(serviceDetailPath(type, id), { state: { from: "sdwan-hub", row } });
    },
    [navigate]
  );

  const filteredData = useMemo(
    () => sdwanHubList.filter(predicate),
    [sdwanHubList, predicate]
  );

  const redirectTo = useMemo(
    () => new URL("/kontrol", window.location.origin).toString(),
    []
  );

  const didInitFm = useRef(false);
  useEffect(() => {
    const ac = new AbortController();
    if (didInitFm.current) return;
    didInitFm.current = true;
    let cancelled = false;
    (async () => {
      try {
        setIsFetchingFm(true);
        const res = await fetchFortimanager(api, {
          tenantId,
          redirectTo,
          signal: ac.signal,
        });
        const payload: FortiManagerResp = res?.data ?? {};
        const d = payload?.data;
        let candidate: string | null = null;

        if (typeof d === "string") {
          candidate = d;
        } else if (d && typeof d === "object") {
          candidate = d.accessUrl ?? d.url ?? null;
        } else {
          candidate = payload?.url ?? null;
        }

        if (!cancelled) setFmUrl(candidate);
      } catch (e) {
        if (!cancelled) setFmUrl(null);
        console.error("fetchFortimanager failed:", e);
      } finally {
        if (!cancelled) setIsFetchingFm(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openCredentialsModal = useCallback((row?: SdwanHub) => {
    setCredentialsModalRow(row ?? null);
    setIsCredentialsOpen(true);
  }, []);

  const closeCredentialsModal = useCallback(() => {
    setIsCredentialsOpen(false);
    setCredentialsModalRow(null);
  }, []);

  const openConnectionsModal = useCallback((row: SdwanHub) => {
    setConnectionsModalRow(row);
  }, []);

  const closeConnectionsModal = useCallback(() => {
    setConnectionsModalRow(null);
  }, []);

  const columns = useMemo<GenericTableColumn<SdwanHub>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: SdwanHub) => row.customer?.name ?? "",
        renderCell: (row: SdwanHub) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }

    return cols.map((col) => {
      if (col.header === "Service ID") {
        return {
          ...col,
          renderCell: (row: SdwanHub) => (
            <button
              {...viewHref(buildDetailPath("sdwanHub", { sdwanId: row.id }))}
              onClick={() => navigate(buildDetailPath("sdwanHub", { sdwanId: row.id }))}
              className="
              hover:text-semantic-text-brand 
              hover:underline
              focus:underline 
              active:underline
              font-medium
              underline
              text-semantic-text-brand
              "
            >
              {row.serviceId}
            </button>
          ),
        };
      }
      if (col.header === "Connection") {
        return {
          ...col,
          accessor: "connections",
          sortAccessor: (row: SdwanHub) => row.connections?.raw ?? "",
          renderCell: (row: SdwanHub) => (
            <ConnectionsCell
              row={row}
              onItemClick={handleConnectionClick}
              itemHref={connectionHref}
              onShowAll={openConnectionsModal}
              full={false}
              showCard={true}
              scrollMaxHeight={140}
              cardVariant="primary"
              cardShadow="sm"
              filterFn={connItemFilterFn}
            />
          ),
        };
      }

      return col;
    });
  }, [
    baseColumns,
    isViewingAllCustomers,
    connItemFilterFn,
    handleConnectionClick,
    navigate,
    openConnectionsModal,
  ]);

  const isDemo = isDemoClient();
  const sdwanColumnVisibility = useColumnVisibility(
    "kontrol:sdwan:columns",
    columns,
    ["Service ID"],
  );
  const tableColumns = isDemo ? sdwanColumnVisibility.visibleColumns : columns;

  if (isLoadingList) {
    return <ListPageSkeleton columns={4} />;
  }
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  if (sdwanHubList.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative">
      <RefreshingIndicator active={isRefreshingList} />
      <GenericCard isCollapsible={false}>
        <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-5 justify-between">
          <div className="flex items-center justify-between gap-3 px-3 sm:px-5 mt-0 sm:mt-5 mb-5 sm:mb-5 w-full">
            <div className="w-full max-w-full sm:max-w-[500px]">
              <GenericSearchbar
                placeholder="Find by attribute or value"
                suggestedKeys={suggestedKeys}
                onFiltersChange={setActiveFilters}
              />
            </div>
            {isDemo && (
              <ColumnVisibilityControl
                columns={columns}
                isVisible={sdwanColumnVisibility.isVisible}
                toggle={sdwanColumnVisibility.toggle}
                requiredIds={["Service ID"]}
              />
            )}
          </div>
          <div className="px-3 sm:px-5 mt-5 sm:mt-2 mb-0 sm:mb-2">
            <GenericButton
              size="md"
              variant="brand"
              onClick={() => openCredentialsModal()}
              label="Management"
            />
          </div>
        </div>
        <div className="px-5 mb-5">
          <GenericTable
            columns={tableColumns}
            data={filteredData}
            initialRowsPerPage={10}
            rowsPerPageOptions={[10, 20, 30]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
          />
        </div>
      </GenericCard>
      <CredentialsModal
        isOpen={isCredentialsOpen}
        row={credentialsModalRow}
        onClose={closeCredentialsModal}
        fmUrl={fmUrl}
        isFetchingFm={isFetchingFm}
        api={api}
        tenantId={tenantId}
        managementBannerText={SDWAN_HUB_MANAGEMENT_BANNER}
      />
      <AllConnectionsModal
        isOpen={!!connectionsModalRow}
        row={connectionsModalRow}
        onClose={closeConnectionsModal}
        onItemClick={handleConnectionClick}
        itemHref={connectionHref}
      />
    </div>
  );
}
